import axios from "axios";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import {
  FaDownload,
  FaFile,
  FaHammer,
  FaSpinner,
  FaTrash,
} from "react-icons/fa";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import stringToColor from "string-to-color";
import api from "../api";
import StatusBadge from "./shared/StatusBadge";
import UrlCopyElement from "./shared/UrlCopyElement";
import { IFile } from "../api/services/file.service";
import { openFileInNewTab } from "../api/utils";

export default function FileSection() {
  let { fileId } = useParams();

  const [file, setFile] = useState<IFile | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (fileId) {
      setLoading(true);
      fetchFile(fileId);
    }
  }, [fileId]);

  async function fetchFile(fileId: string) {
    try {
      setLoading(true);
      const { data } = await api.file.getFile(fileId);
      if (data.result) {
        setFile(data.result);
        setLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  async function deleteFile(fileId: string) {
    try {
      setLoading(true);
      await api.file.deleteFile(fileId);
      setLoading(false);
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  async function changeFileStatus(fileId: string, status: string) {
    try {
      setLoading(true);
      const { data } = await api.file.updateFilePartially(fileId, {
        status: status,
      });
      if (data.result) {
        setFile(data.result);
        setLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  function determineStatusColor(status: string | null) {
    switch (status) {
      case "READY":
        return "bg-green-200 text-green-800";
      case "CREATED":
        return "bg-blue-200 text-blue-800";
      case "IN_UPLOAD":
        return "bg-orange-200 text-orange-800";
      case "ERROR":
        return "bg-red-200 text-red-800";
      default:
        return "bg-slate-300 text-slate-800";
    }
  }

  return (
    <div className="flex flex-col rounded-2xl shadow-xl bg-white grow overflow-hidden">
      <div className="flex items-center p-4 w-full border-b justify-between">
        <div className="flex items-center">
          <div className="flex items-center mr-10">
            <div className="">
              <FaFile size={20} className="text-red-700" />
            </div>
            <span className="ml-2 font-medium leading-6 text-slate-700 text-lg flex items-center">
              <span className="text-slate-700">File Information</span>
            </span>
          </div>
        </div>
        <div className="flex space-x-2">
          {/* <div className="font-semibold text-slate-700">{teams.length}</div> */}
          <div className="flex space-x-2">
            <button
              className="group flex w-full items-center text-sm"
              onClick={() => deleteFile(fileId || "")}
            >
              <FaTrash
                className="mr-2 h-5 w-5 hover:scale-110"
                aria-hidden="true"
              />
            </button>
            <button
              onClick={() =>
                openFileInNewTab(fileId || "", file?.tenantId || "")
              }
              type="button"
              className="group flex w-full items-center text-sm"
            >
              <FaDownload className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      {loading && (
        <div className="flex items-center justify-center p-20 text-slate-500">
          <FaSpinner size={30} className="animate-spin" />
        </div>
      )}
      {!loading && file !== null && (
        <div>
          <div className="px-3 md:px-4 py-5 sm:px-6 flex space-x-4 bg-slate-100">
            <FaFile className="h-12 w-12" color={stringToColor(file.id)} />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {file.name} <small>({file.contentType})</small>
              </h3>
              <div className="flex-col">
                <p className="mt-1 max-w-2xl text-sm text-gray-500">
                  {(file.size / 1000000).toFixed(3)} mb
                </p>
              </div>
            </div>
          </div>
          <div className="">
            <dl>
              <div className="bg-white px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">File Name</dt>
                <dd className="flex justify-between items-center space-x-2 mt-1 text-sm font-semibold text-gray-900 sm:mt-0 sm:col-span-2">
                  {file.name}
                  <StatusBadge
                    status={file?.role || ""}
                    // className={"bg-amber-600 text-white"}
                  />
                </dd>
              </div>
              <div className="bg-slate-100 px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">PrimaryId</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <UrlCopyElement url={file.id || ""} />
                </dd>
              </div>
              <div className="bg-white px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">ParentId</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <UrlCopyElement url={file.parentId} />
                </dd>
              </div>
              <div className="bg-slate-100 px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Creation</dt>
                <dd className="flex justify-between items-center space-x-2 mt-1 text-sm font-semibold text-gray-900 sm:mt-0 sm:col-span-2">
                  <div>
                    {format(new Date(file.createdAt), "dd.MM.yyyy HH:mm:ss")}
                  </div>
                  <div className="flex space-x-4">
                    <StatusBadge
                      status={file.status}
                      className={determineStatusColor(file.status)}
                    />
                    {file.status !== "READY" && (
                      <button
                        className="items-center rounded-md text-sm"
                        onClick={() => changeFileStatus(fileId || "", "READY")}
                      >
                        <FaHammer
                          className="h-5 w-5 hover:scale-110 text-amber-600"
                          aria-hidden="true"
                        />
                      </button>
                    )}
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
      {!loading && file === null && (
        <div className="flex flex-col items-center justify-center p-8 text-slate-500">
          <div className="font-semibold text-slate-700 mb-2">
            File Not Found
          </div>
          <div className="inline-flex items-center space-x-2">
            <div>File not found...</div>
          </div>
        </div>
      )}
    </div>
  );
}
